import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ServiceLocationInfo } from '../types/firestore';
import { serviceProviderService } from '../services/serviceProviderService';
import {
  buildServiceDiscoverySearchParams,
  DEFAULT_DISCOVERY_INCLUDE_UNPRICED,
  DEFAULT_DISCOVERY_SORT,
  filterServices,
  getServiceDiscoveryFiltersFromSearchParams,
  getUniqueCities,
  hasActiveHourlyRateFilter,
  mergeServiceDiscoverySearchParams,
  sortServices,
  type ServiceDiscoveryFilters,
  type ServiceDiscoveryIssueType,
} from '../utils/serviceDiscovery';
import type { SmartSearchSelection } from '../utils/smartSearch';

export const useServiceDiscovery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState<ServiceLocationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo(
    () => getServiceDiscoveryFiltersFromSearchParams(searchParams),
    [searchParams]
  );
  const {
    query,
    city,
    sortBy,
    hourlyRateMax,
    includeUnpriced,
    includeUnpricedParamMode,
    issueType,
    smartSelection,
  } = filters;
  const canonicalSearchParams = useMemo(
    () => buildServiceDiscoverySearchParams(searchParams, filters),
    [filters, searchParams]
  );

  useEffect(() => {
    if (canonicalSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(canonicalSearchParams, { replace: true });
  }, [canonicalSearchParams, searchParams, setSearchParams]);

  const updateParams = useCallback(
    (
      next: Partial<ServiceDiscoveryFilters>,
      options?: {
        replace?: boolean;
      }
    ) => {
      const nextSearchParams = mergeServiceDiscoverySearchParams(searchParams, filters, next);
      if (nextSearchParams.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(nextSearchParams, options?.replace ? { replace: true } : undefined);
    },
    [filters, searchParams, setSearchParams]
  );

  const selectSmartSearchSuggestion = useCallback(
    (selection: SmartSearchSelection | null) => {
      if (!selection) {
        updateParams({ query: '', smartSelection: null, issueType: null });
        return;
      }

      updateParams({
        query: selection.inputSelectedLabel,
        smartSelection: selection,
        issueType: selection.intent || null,
      });
    },
    [updateParams]
  );

  const setIssueTypeFilter = useCallback(
    (value: ServiceDiscoveryIssueType) => updateParams({ issueType: value }),
    [updateParams]
  );

  const setCityFilter = useCallback(
    (value: string) => updateParams({ city: value.trim() }),
    [updateParams]
  );

  const setSortFilter = useCallback(
    (value: ServiceDiscoveryFilters['sortBy']) => updateParams({ sortBy: value }),
    [updateParams]
  );

  const setHourlyRateMaxFilter = useCallback(
    (value: number | null) => {
      if (value === null) {
        updateParams({
          hourlyRateMax: null,
          includeUnpriced: DEFAULT_DISCOVERY_INCLUDE_UNPRICED,
          includeUnpricedParamMode: 'implicit',
        });
        return;
      }

      const shouldPreserveExplicitIncludeUnpriced =
        hasActiveHourlyRateFilter(hourlyRateMax) &&
        includeUnpriced &&
        includeUnpricedParamMode === 'explicit-on';

      updateParams({
        hourlyRateMax: value,
        includeUnpriced: shouldPreserveExplicitIncludeUnpriced,
        includeUnpricedParamMode: shouldPreserveExplicitIncludeUnpriced
          ? 'explicit-on'
          : 'explicit-off',
      });
    },
    [hourlyRateMax, includeUnpriced, includeUnpricedParamMode, updateParams]
  );

  const setIncludeUnpricedFilter = useCallback(
    (value: boolean) =>
      updateParams({
        includeUnpriced: value,
        includeUnpricedParamMode: hasActiveHourlyRateFilter(hourlyRateMax)
          ? value
            ? 'explicit-on'
            : 'explicit-off'
          : 'implicit',
      }),
    [hourlyRateMax, updateParams]
  );

  const clearHourlyRateFilter = useCallback(
    () =>
      updateParams({
        hourlyRateMax: null,
        includeUnpriced: DEFAULT_DISCOVERY_INCLUDE_UNPRICED,
        includeUnpricedParamMode: 'implicit',
      }),
    [updateParams]
  );

  const resetFilters = useCallback(() => {
    updateParams({
      query: '',
      city: '',
      sortBy: DEFAULT_DISCOVERY_SORT,
      hourlyRateMax: null,
      includeUnpriced: DEFAULT_DISCOVERY_INCLUDE_UNPRICED,
      includeUnpricedParamMode: 'implicit',
      issueType: null,
      smartSelection: null,
    });
  }, [updateParams]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceProviderService.getActiveServiceProvidersLocations();
      setServices(data);
    } catch (fetchError) {
      console.error('Blad pobierania serwisow:', fetchError);
      setError('Nie udalo sie pobrac serwisow. Sprobuj ponownie za chwile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const filteredServices = useMemo(
    () =>
      filterServices(services, {
        query,
        city,
        hourlyRateMax,
        includeUnpriced,
        issueType,
        smartSelection,
      }),
    [city, hourlyRateMax, includeUnpriced, issueType, query, services, smartSelection]
  );
  const queryAndCityFilteredServices = useMemo(
    () =>
      filterServices(services, {
        query,
        city,
        hourlyRateMax: null,
        includeUnpriced: DEFAULT_DISCOVERY_INCLUDE_UNPRICED,
        issueType,
        smartSelection,
      }),
    [city, issueType, query, services, smartSelection]
  );
  const sortedServices = useMemo(
    () => sortServices(filteredServices, sortBy, smartSelection, issueType),
    [filteredServices, issueType, smartSelection, sortBy]
  );
  const uniqueCities = useMemo(() => getUniqueCities(services), [services]);

  const searchState = useMemo(
    () => ({
      totalCount: services.length,
      filteredCount: filteredServices.length,
      hasActiveFilters: Boolean(
        query || city || hasActiveHourlyRateFilter(hourlyRateMax) || issueType || smartSelection
      ),
      hasSmartSelection: Boolean(smartSelection),
      hasActiveHourlyRateFilter: hasActiveHourlyRateFilter(hourlyRateMax),
      hasExplicitHourlyRateMode: hasActiveHourlyRateFilter(hourlyRateMax) && !includeUnpriced,
      isHourlyRateNoResults:
        hasActiveHourlyRateFilter(hourlyRateMax) &&
        filteredServices.length === 0 &&
        queryAndCityFilteredServices.length > 0,
      isSmartNoResults:
        Boolean(smartSelection) &&
        filteredServices.length === 0 &&
        !(hasActiveHourlyRateFilter(hourlyRateMax) && queryAndCityFilteredServices.length > 0),
    }),
    [
      city,
      filteredServices.length,
      hourlyRateMax,
      includeUnpriced,
      issueType,
      query,
      queryAndCityFilteredServices.length,
      smartSelection,
      services.length,
    ]
  );

  return {
    filters,
    services,
    filteredServices,
    sortedServices,
    uniqueCities,
    loading,
    error,
    query,
    city,
    sortBy,
    hourlyRateMax,
    includeUnpriced,
    issueType,
    smartSelection,
    searchState,
    setSortFilter,
    setCityFilter,
    setIssueTypeFilter,
    setHourlyRateMaxFilter,
    setIncludeUnpricedFilter,
    selectSmartSearchSuggestion,
    setQueryFromSearch: selectSmartSearchSuggestion,
    clearHourlyRateFilter,
    resetFilters,
    refresh: fetchServices,
    searchParams,
  };
};
